// First terminal — types out character-by-character on first viewport hit.
// Subsequent terminals reveal instantly (the typing effect is the reader's
// induction; repeating it would be tedious).

const CHAR_MS = 28;
const LINE_PAUSE_MS = 280;

export function initFirstTerminal(): void {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;

  // The first .dlg in The Door section is the canonical "first terminal".
  const door = document.querySelector<HTMLElement>('.s--door');
  if (!door) return;
  const dlg = door.querySelector<HTMLElement>('.dlg:not(.dlg--human)');
  if (!dlg) return;

  // Capture original lines, blank them out, queue typing.
  const lines = Array.from(dlg.querySelectorAll<HTMLElement>('p'));
  const originals = lines.map((p) => p.innerHTML);
  lines.forEach((p) => {
    p.innerHTML = '';
    p.classList.add('typing');
  });
  // Mark dlg so CSS can show a placeholder height while we wait.
  dlg.classList.add('typing-pending');

  let started = false;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting || started) return;
        started = true;
        io.unobserve(e.target);
        runTyping(lines, originals, dlg);
      });
    },
    { threshold: 0.4 }
  );
  io.observe(dlg);
}

async function runTyping(lines: HTMLElement[], originals: string[], dlg: HTMLElement) {
  dlg.classList.remove('typing-pending');
  for (let i = 0; i < lines.length; i++) {
    await typeLine(lines[i], originals[i]);
    if (i < lines.length - 1) await sleep(LINE_PAUSE_MS);
  }
}

function typeLine(el: HTMLElement, html: string): Promise<void> {
  return new Promise((resolve) => {
    // Strip tags for the typing effect; we'll reinstate the full HTML at end.
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const plain = tmp.textContent ?? '';
    let i = 0;
    function step() {
      i++;
      el.textContent = plain.slice(0, i);
      if (i >= plain.length) {
        // Restore the original markup so any <code>/<em> styling lights up.
        el.innerHTML = html;
        el.classList.remove('typing');
        resolve();
        return;
      }
      setTimeout(step, CHAR_MS);
    }
    step();
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
